import moment from 'moment';
import { useEffect, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '../../components-v2/Popover';
import { CalendarIcon } from '../../components/svgs/icons/CalendarIcon';
import { Button } from '../Button';
import { ButtonGroup } from '../ButtonGroup';
import { Calendar } from '../Calendar';
import { Input } from '../Input';

type Props = {
	value: string;
	onChange: (value: string) => void;
	disabled?: boolean;
};

// Common date formats to try parsing
const DATE_FORMATS = [
	'YYYY-MM-DD',
	'MM/DD/YYYY',
	'M/D/YYYY',
	'MM-DD-YYYY',
	'M-D-YYYY',
	'MM/DD/YY',
	'M/D/YY',
	'MM-DD-YY',
	'M-D-YY',
	'YYYY/MM/DD',
];

// A full ISO datetime (the shape the API round-trips) normalizes down to the YYYY-MM-DD the field edits.
const normalizeExternalValue = (raw: string) => {
	const parsed = moment(raw, moment.ISO_8601, true);
	return parsed.isValid() ? parsed.format('YYYY-MM-DD') : raw;
};

export const DateInput = ({ value, onChange, disabled }: Props) => {
	const [localValue, setLocalValue] = useState(() => normalizeExternalValue(value));
	const [isCalendarOpen, setIsCalendarOpen] = useState(false);
	const placeholder = 'YYYY-MM-DD';

	// Sync local state with prop when prop changes (e.g., after API response)
	useEffect(() => {
		setLocalValue(normalizeExternalValue(value));
	}, [value]);

	const handleCalendarSelect = (newValue: string) => {
		setLocalValue(newValue); // Update immediately for responsiveness
		onChange(newValue); // Trigger API call immediately for calendar selection
		setIsCalendarOpen(false); // Close the calendar after selection
	};

	const handleInputChange = (newValue: string) => {
		setLocalValue(newValue); // Update display immediately, but don't trigger API yet
	};

	const handleInputBlur = () => {
		// Only trigger onChange when user is done typing and value has changed
		if (localValue === normalizeExternalValue(value)) {
			return;
		}

		// If empty, pass through as-is
		if (!localValue.trim()) {
			onChange(localValue);
			return;
		}

		// Try to parse with various formats and normalize to YYYY-MM-DD
		const parsed = moment(localValue, DATE_FORMATS, true);
		if (parsed.isValid()) {
			const normalized = parsed.format('YYYY-MM-DD');
			setLocalValue(normalized); // Update display to normalized format
			onChange(normalized);
		} else {
			// Pass through as-is and let the handler decide what to do
			onChange(localValue);
		}
	};

	return (
		<ButtonGroup>
			<Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
				<PopoverTrigger asChild>
					<Button variant='secondary' disabled={disabled}>
						<CalendarIcon />
					</Button>
				</PopoverTrigger>
				<PopoverContent side='bottom'>
					<Calendar
						mode='single'
						selected={localValue && moment(localValue).isValid() ? moment(localValue).toDate() : undefined}
						defaultMonth={localValue && moment(localValue).isValid() ? moment(localValue).toDate() : new Date()}
						onSelect={day => {
							handleCalendarSelect(moment(day).format('YYYY-MM-DD'));
						}}
					/>
				</PopoverContent>
			</Popover>
			<Input
				value={localValue}
				onChange={e => handleInputChange(e.target.value)}
				onBlur={handleInputBlur}
				placeholder={placeholder}
				disabled={disabled}
			/>
		</ButtonGroup>
	);
};
